import { useState, useCallback } from 'react';

export type ModalType =
  | 'start'
  | 'edit'
  | 'delete'
  | 'duplicate'
  | 'end'
  | 'archive'
  | 'view'
  | 'setWinnerInstructions';

export interface ModalStates {
  start: boolean;
  edit: boolean;
  delete: boolean;
  duplicate: boolean;
  end: boolean;
  archive: boolean;
  view: boolean;
  setWinnerInstructions: boolean;
}

export interface WinnerModalState {
  show: boolean;
  id: number | undefined;
  text?: string;
}

export interface ModalActions {
  open: (modal: ModalType) => void;
  close: (modal: ModalType) => void;
  closeAll: () => void;
  toggle: (modal: ModalType) => void;
}

export const useAbTestModals = () => {
  const [modalStates, setModalStates] = useState<ModalStates>({
    start: false,
    edit: false,
    delete: false,
    duplicate: false,
    end: false,
    archive: false,
    view: false,
    setWinnerInstructions: false,
  });

  const [setAsWinnerOpen, setSetAsWinnerOpen] = useState<WinnerModalState>({
    show: false,
    id: undefined,
  });

  const [forceSetAsWinnerOpen, setForceSetAsWinnerOpen] = useState<WinnerModalState>({
    show: false,
    id: undefined,
    text: undefined,
  });

  const open = useCallback((modal: ModalType) => {
    setModalStates((prev) => ({ ...prev, [modal]: true }));
  }, []);

  const close = useCallback((modal: ModalType) => {
    setModalStates((prev) => ({ ...prev, [modal]: false }));
  }, []);

  const closeAll = useCallback(() => {
    setModalStates({
      start: false,
      edit: false,
      delete: false,
      duplicate: false,
      end: false,
      archive: false,
      view: false,
      setWinnerInstructions: false,
    });
    setSetAsWinnerOpen({ show: false, id: undefined });
    setForceSetAsWinnerOpen({ show: false, id: undefined, text: undefined });
  }, []);

  const toggle = useCallback((modal: ModalType) => {
    setModalStates((prev) => ({ ...prev, [modal]: !prev[modal] }));
  }, []);

  const modalActions: ModalActions = {
    open,
    close,
    closeAll,
    toggle,
  };

  return {
    modalStates,
    modalActions,
    setAsWinnerOpen,
    setSetAsWinnerOpen,
    forceSetAsWinnerOpen,
    setForceSetAsWinnerOpen,
  };
};
